import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import CountUp from "@/components/CountUp";
import { fmtMoney, fmtPctPlain, fmtNum } from "@/lib/format";
import {
    Play, Pause, ArrowLeft, ArrowRight, X, ArrowsOut, ArrowsIn,
    TrendUp, TrendDown, Warning, Sparkle, Buildings, GlobeHemisphereEast, ChartBar, Rocket,
} from "@phosphor-icons/react";

const SLIDE_DURATION_MS = 22000; // 22s per slide

export default function BoardMeeting() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const [fs, setFs] = useState(false);
    const progressRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        client.get("/dashboard/overview").then((r) => setData(r.data));
    }, []);

    // Auto-advance timer
    useEffect(() => {
        if (paused || !data) return;
        const start = Date.now();
        const tick = () => {
            if (!progressRef.current) return;
            const elapsed = Date.now() - start;
            const pct = Math.min(100, (elapsed / SLIDE_DURATION_MS) * 100);
            progressRef.current.style.width = `${pct}%`;
            if (elapsed >= SLIDE_DURATION_MS) {
                setIdx((i) => (i + 1) % SLIDES.length);
            }
        };
        const id = setInterval(tick, 100);
        return () => clearInterval(id);
    }, [idx, paused, data]);

    // Reset progress bar on slide change
    useEffect(() => {
        if (progressRef.current) progressRef.current.style.width = "0%";
    }, [idx]);

    // Keyboard controls
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight" || e.key === "PageDown") { e.preventDefault(); setIdx((i) => (i + 1) % SLIDES.length); }
            else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length); }
            else if (e.key === " " || e.key === "Spacebar") { e.preventDefault(); setPaused((p) => !p); }
            else if (e.key === "Escape") { exitFullscreen(); nav("/"); }
            else if (e.key.toLowerCase() === "f") { toggleFullscreen(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []); // eslint-disable-line

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen?.().then(() => setFs(true)).catch(() => {});
        } else {
            exitFullscreen();
        }
    };
    const exitFullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen?.().then(() => setFs(false)).catch(() => {});
    };
    useEffect(() => {
        const onFs = () => setFs(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onFs);
        return () => document.removeEventListener("fullscreenchange", onFs);
    }, []);

    if (!data) return <div className="min-h-screen bg-fp-ink text-white flex items-center justify-center font-serif text-3xl">Preparing board briefing…</div>;

    const Slide = SLIDES[idx].component;

    return (
        <div ref={containerRef} className="fixed inset-0 z-[100] bg-fp-ink text-white flex flex-col overflow-hidden" data-testid="board-meeting-container">
            {/* Progress bar */}
            <div className="h-0.5 bg-white/10 w-full">
                <div ref={progressRef} className="h-full bg-fp-saffron transition-none" style={{ width: "0%" }} />
            </div>

            {/* Top bar */}
            <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border border-fp-saffron flex items-center justify-center font-serif text-sm">Fp</div>
                    <div>
                        <div className="font-serif text-base sm:text-lg leading-tight">Finance Performance</div>
                        <div className="font-sans text-[9px] uppercase tracking-[0.3em] text-white/60">Board Briefing · {data.snapshot.period_label}</div>
                    </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
                    <span className="w-1.5 h-1.5 rounded-full bg-fp-saffron animate-pulse" />
                    <span>Slide {idx + 1} of {SLIDES.length}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button data-testid="pres-fullscreen" onClick={toggleFullscreen} className="p-2 border border-white/20 hover:border-white/60 transition-colors" title="Fullscreen (F)">
                        {fs ? <ArrowsIn size={14} /> : <ArrowsOut size={14} />}
                    </button>
                    <button data-testid="pres-exit" onClick={() => { exitFullscreen(); nav("/"); }} className="p-2 border border-white/20 hover:border-white/60 transition-colors" title="Exit (Esc)">
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* Slide surface */}
            <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-8 overflow-y-auto" key={idx}>
                <div className="slide-in w-full max-w-6xl">
                    <Slide data={data} />
                </div>
            </div>

            {/* Bottom control bar */}
            <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-t border-white/10">
                <button data-testid="pres-prev" onClick={() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length)} className="inline-flex items-center gap-2 font-sans text-xs text-white/70 hover:text-white transition-colors">
                    <ArrowLeft size={14} /> Prev
                </button>
                <div className="flex items-center gap-2">
                    <button data-testid="pres-pause" onClick={() => setPaused((p) => !p)} className="p-2.5 bg-white/10 hover:bg-white/20 transition-colors" title="Pause / Play (Space)">
                        {paused ? <Play size={14} weight="fill" /> : <Pause size={14} weight="fill" />}
                    </button>
                    <div className="hidden sm:flex items-center gap-1">
                        {SLIDES.map((_, i) => (
                            <button key={i} onClick={() => setIdx(i)} className={`w-6 h-0.5 transition-colors ${i === idx ? "bg-fp-saffron" : "bg-white/20 hover:bg-white/40"}`} data-testid={`pres-dot-${i}`} aria-label={`Go to slide ${i + 1}`} />
                        ))}
                    </div>
                </div>
                <button data-testid="pres-next" onClick={() => setIdx((i) => (i + 1) % SLIDES.length)} className="inline-flex items-center gap-2 font-sans text-xs text-white/70 hover:text-white transition-colors">
                    Next <ArrowRight size={14} />
                </button>
            </div>

            {/* Hint (fades out after 6s) */}
            <div className="pointer-events-none fixed bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-widest text-white/40 hidden sm:block">
                Space · Pause  ·  ← → · Navigate  ·  F · Fullscreen  ·  Esc · Exit
            </div>
        </div>
    );
}

/* ================ SLIDES ================ */

const SlideHeader = ({ eyebrow, title, subtitle }) => (
    <div className="mb-8 sm:mb-12">
        <div className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.35em] text-fp-saffron mb-3">{eyebrow}</div>
        <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white">{title}</h1>
        {subtitle && <p className="font-sans text-sm sm:text-lg text-white/60 mt-4 max-w-3xl">{subtitle}</p>}
    </div>
);

const CoverSlide = ({ data }) => (
    <div className="text-center">
        <div className="inline-flex items-center gap-3 font-sans text-[10px] uppercase tracking-[0.35em] text-white/50 mb-8">
            <span className="w-8 h-px bg-fp-saffron" />
            Board Briefing · {data.snapshot.period_label}
            <span className="w-8 h-px bg-fp-saffron" />
        </div>
        <h1 className="font-serif text-5xl sm:text-8xl leading-[0.95] tracking-tight text-white mb-6">Finance Performance</h1>
        <div className="font-serif text-3xl sm:text-5xl text-fp-saffron italic mb-10">Prototype.</div>
        <div className="max-w-2xl mx-auto">
            <p className="font-sans text-sm sm:text-base text-white/70 leading-relaxed">
                Consolidated performance across <strong className="text-white">168 entities</strong>,{" "}
                <strong className="text-white">7 divisions</strong> and{" "}
                <strong className="text-white">16 jurisdictions</strong>.
            </p>
            <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/40 mt-12">Illustrative · Concept prototype</div>
        </div>
    </div>
);

const KpiSlide = ({ data }) => {
    const s = data.snapshot;
    const items = [
        { label: "Revenue", value: s.revenue.value_musd, prefix: "$", suffix: "M", accent: "text-fp-saffron", note: `${fmtPctPlain(s.revenue.yoy_pct)} YoY · vs Bud ${fmtPctPlain(s.revenue.budget_variance_pct)}` },
        { label: "EBITDA", value: s.ebitda.value_musd, prefix: "$", suffix: "M", accent: "text-white", note: `Margin ${fmtPctPlain(s.ebitda.margin_pct)}` },
        { label: "Free Cash Flow", value: s.free_cash_flow.value_musd, prefix: "$", suffix: "M", accent: "text-white", note: `Monthly generation` },
        { label: "Return on Invested Capital", value: s.roic_pct, suffix: "%", decimals: 1, accent: "text-white", note: `ROE ${fmtPctPlain(s.roe_pct)}` },
    ];
    return (
        <div>
            <SlideHeader eyebrow="Group KPIs · MTD" title="Headline Performance" subtitle="Consolidated group snapshot for the reporting period." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {items.map((k) => (
                    <div key={k.label} className="border-l-2 border-fp-saffron pl-6 py-2">
                        <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">{k.label}</div>
                        <div className={`font-serif text-5xl sm:text-6xl tracking-tight ${k.accent}`}>
                            <CountUp value={k.value} decimals={k.decimals || 0} prefix={k.prefix || ""} suffix={k.suffix || ""} />
                        </div>
                        <div className="font-mono text-xs text-white/60 mt-2">{k.note}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const BusinessPerfSlide = ({ data }) => {
    const sectors = [...data.sector_breakdown].sort((a, b) => b.yoy_growth_pct - a.yoy_growth_pct);
    const top = sectors[0], watch = sectors[Math.floor(sectors.length / 2)], under = sectors[sectors.length - 1];
    const items = [
        { tone: "top", label: "Top Performer", name: top.sector_name, metric: `${fmtPctPlain(top.yoy_growth_pct)} YoY · ${fmtPctPlain(top.ebitda_margin_pct)} margin`, Icon: TrendUp, color: "border-fp-success text-fp-success" },
        { tone: "watch", label: "Watchlist", name: watch.sector_name, metric: `${fmtPctPlain(watch.yoy_growth_pct)} YoY · ${fmtPctPlain(watch.ebitda_margin_pct)} margin`, Icon: Warning, color: "border-fp-saffron text-fp-saffron" },
        { tone: "under", label: "Underperformer", name: under.sector_name, metric: `${fmtPctPlain(under.yoy_growth_pct)} YoY · ${fmtPctPlain(under.ebitda_margin_pct)} margin`, Icon: TrendDown, color: "border-fp-terracotta text-fp-terracotta" },
    ];
    return (
        <div>
            <SlideHeader eyebrow="Sector Ranking" title="Business Performance" subtitle="Ranked by year-on-year revenue growth." />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {items.map((it) => {
                    const Icon = it.Icon;
                    return (
                        <div key={it.label} className={`border-t-2 ${it.color} pt-6`}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/50">{it.label}</div>
                                <Icon size={18} weight="fill" className={it.color} />
                            </div>
                            <div className="font-serif text-3xl sm:text-4xl text-white leading-tight mb-3">{it.name}</div>
                            <div className="font-mono text-xs text-white/60">{it.metric}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const SectorContributionSlide = ({ data }) => {
    const sectors = [...data.sector_breakdown].sort((a, b) => b.revenue_musd - a.revenue_musd).slice(0, 6);
    const max = Math.max(...sectors.map((s) => s.revenue_musd));
    return (
        <div>
            <SlideHeader eyebrow="Revenue Contribution" title="By Sector" subtitle="Top 6 sectors by contribution to consolidated revenue." />
            <div className="space-y-4">
                {sectors.map((s) => (
                    <div key={s.sector_id}>
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="font-serif text-lg sm:text-2xl text-white">{s.sector_name}</div>
                            <div className="font-mono text-sm sm:text-lg text-fp-saffron">{fmtMoney(s.revenue_musd, { decimals: 0 })}</div>
                        </div>
                        <div className="h-2 bg-white/10 relative overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-fp-saffron to-fp-terracotta transition-all duration-1000" style={{ width: `${(s.revenue_musd / max) * 100}%` }} />
                        </div>
                        <div className="flex justify-between font-mono text-[10px] text-white/40 mt-1">
                            <span>Margin {fmtPctPlain(s.ebitda_margin_pct)}</span>
                            <span className={s.yoy_growth_pct >= 0 ? "text-fp-success" : "text-fp-terracotta"}>{s.yoy_growth_pct >= 0 ? "+" : ""}{s.yoy_growth_pct}% YoY</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const GeoSlide = ({ data }) => {
    const countries = [...data.country_breakdown].sort((a, b) => b.revenue_musd - a.revenue_musd).slice(0, 8);
    return (
        <div>
            <SlideHeader eyebrow="Geographic Footprint" title="Top Markets" subtitle="Revenue by country of operation." />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {countries.map((c) => (
                    <div key={c.country_code} className="border border-white/15 p-4 sm:p-5">
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{c.region}</div>
                            <div className="font-mono text-[10px] text-white/40">{c.country_code}</div>
                        </div>
                        <div className="font-serif text-lg sm:text-xl text-white mb-1">{c.country_name}</div>
                        <div className="font-mono text-xl sm:text-2xl text-fp-saffron">{fmtMoney(c.revenue_musd, { decimals: 0 })}</div>
                        <div className="font-mono text-[10px] text-white/40 mt-1">{c.entity_count} entities</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TransformationSlide = () => {
    const items = [
        { n: "Working Capital Optimisation", pct: 56, t: "success", target: "$25M", r: "$14M" },
        { n: "Procurement Efficiency", pct: 33, t: "warn", target: "$12M", r: "$4M" },
        { n: "Reporting Automation", pct: 60, t: "success", target: "30% effort", r: "18%" },
        { n: "Commercial Productivity", pct: 33, t: "danger", target: "$18M", r: "$6M" },
    ];
    const toneCls = { success: "bg-fp-success", warn: "bg-fp-saffron", danger: "bg-fp-terracotta" };
    return (
        <div>
            <SlideHeader eyebrow="Value Realised" title="Transformation Portfolio" subtitle="Active initiatives · progress vs target." />
            <div className="space-y-6">
                {items.map((i) => (
                    <div key={i.n}>
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="font-serif text-lg sm:text-2xl text-white">{i.n}</div>
                            <div className="font-mono text-sm text-white/60">{i.r} / {i.target}</div>
                        </div>
                        <div className="h-2 bg-white/10 overflow-hidden">
                            <div className={`h-full ${toneCls[i.t]} transition-all duration-1000`} style={{ width: `${i.pct}%` }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const RisksSlide = () => {
    const risks = [
        { title: "FX exposure — EGP / TRY", desc: "Continued lira weakness weighing on Türkiye margins; Egypt IMF facility mitigating." },
        { title: "Working capital drag in Retail", desc: "DIO stretched to 96 days; Red Sea disruption impact through Q1." },
        { title: "Capital adequacy pressure", desc: "SAMA CAR floor raised to 10.5%; Financial Capital Group at 11.2% cushion." },
        { title: "OECD Pillar-2 minimum tax", desc: "Effective tax rate uplift of ~140bps across GCC entities from Jan 2026." },
    ];
    return (
        <div>
            <SlideHeader eyebrow="Board Attention" title="Key Risks & Watchouts" subtitle="Items requiring active oversight this cycle." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {risks.map((r, i) => (
                    <div key={i} className="border-l-4 border-fp-terracotta pl-5 py-2">
                        <div className="font-serif text-xl sm:text-2xl text-white mb-2 leading-tight">{r.title}</div>
                        <div className="font-sans text-sm text-white/60 leading-relaxed">{r.desc}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const OutlookSlide = ({ data }) => {
    const s = data.snapshot;
    const items = [
        { label: "FY Revenue Run-rate", value: fmtMoney(s.revenue.value_musd * 12, { decimals: 0 }), note: "Annualised" },
        { label: "Budget Achievement", value: fmtPctPlain(s.revenue.budget_variance_pct), note: "Latest month" },
        { label: "FCF Coverage", value: fmtMoney(s.free_cash_flow.value_musd, { decimals: 0 }), note: "Monthly generation" },
    ];
    return (
        <div>
            <SlideHeader eyebrow="Forward View" title="Outlook · Scenario" subtitle="Base-case trajectory with modelled sensitivities." />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
                {items.map((i) => (
                    <div key={i.label}>
                        <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">{i.label}</div>
                        <div className="font-serif text-4xl sm:text-5xl text-fp-saffron">{i.value}</div>
                        <div className="font-mono text-xs text-white/50 mt-2">{i.note}</div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-white/10">
                {[
                    { name: "Base", rev: "+5.0%", ebitda: "+4.2%", tone: "text-white" },
                    { name: "Upside", rev: "+9.0%", ebitda: "+8.5%", tone: "text-fp-success" },
                    { name: "Downside", rev: "-2.0%", ebitda: "-3.8%", tone: "text-fp-terracotta" },
                ].map((sc) => (
                    <div key={sc.name} className="border border-white/15 p-4">
                        <div className="font-sans text-[10px] uppercase tracking-widest text-white/50 mb-2">{sc.name} Scenario</div>
                        <div className={`font-mono text-lg ${sc.tone}`}>Rev {sc.rev}</div>
                        <div className="font-mono text-xs text-white/50">EBITDA {sc.ebitda}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ClosingSlide = () => (
    <div className="text-center">
        <div className="inline-flex items-center gap-3 font-sans text-[10px] uppercase tracking-[0.35em] text-white/50 mb-8">
            <span className="w-8 h-px bg-fp-saffron" />
            End of Briefing
            <span className="w-8 h-px bg-fp-saffron" />
        </div>
        <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight text-white mb-8">Discussion.</h1>
        <p className="font-sans text-sm sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            Reasoned insight for finance leadership — AI-authored narrative, driver-based scenarios and cross-entity analytics.
        </p>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-16">Concept prototype  ·  Radhika Mehra</div>
    </div>
);

const SLIDES = [
    { component: CoverSlide },
    { component: KpiSlide },
    { component: BusinessPerfSlide },
    { component: SectorContributionSlide },
    { component: GeoSlide },
    { component: TransformationSlide },
    { component: RisksSlide },
    { component: OutlookSlide },
    { component: ClosingSlide },
];
